import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { courses } from "@/mocks/academyData";
import EnrollModal from "./EnrollModal";

export default function CourseDetail() {
  const { slug } = useParams<{ slug: string }>();
  const course = courses.find((c) => c.slug === slug);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "curriculum" | "instructor">("overview");

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Course not found.</p>
          <Link to="/academy" className="text-orange-500 font-semibold cursor-pointer">Back to Academy</Link>
        </div>
      </div>
    );
  }

  const discount = Math.round(((course.originalPrice - course.price) / course.originalPrice) * 100);

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-16 bg-gray-950 overflow-hidden">
        <div className="absolute inset-0">
          <img src={course.image} alt={course.title} className="w-full h-full object-cover object-top opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/98 via-gray-950/90 to-gray-950/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <Link to="/academy" className="inline-flex items-center gap-1.5 text-gray-400 hover:text-white text-sm mb-6 cursor-pointer transition-colors">
            <i className="ri-arrow-left-line" /> Back to Academy
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
            {/* Left */}
            <div className="lg:col-span-2">
              <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full mb-4 ${course.categoryBg} ${course.categoryText}`}>
                {course.category}
              </span>
              <h1 className="text-3xl md:text-5xl font-black text-white mb-3 leading-tight">{course.title}</h1>
              <p className="text-gray-300 text-lg mb-6">{course.tagline}</p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-300 mb-6">
                <span className="flex items-center gap-1.5"><i className="ri-time-line text-orange-400" />{course.duration}</span>
                <span className="flex items-center gap-1.5"><i className="ri-bar-chart-2-line text-orange-400" />{course.level}</span>
                <span className="flex items-center gap-1.5"><i className="ri-live-line text-orange-400" />{course.format}</span>
                <span className="flex items-center gap-1.5"><i className="ri-group-line text-orange-400" />{course.students.toLocaleString()} students</span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-amber-400" />{course.rating} ({course.reviews} reviews)
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-black text-white bg-gradient-to-br ${course.instructor.color}`}>
                  {course.instructor.avatar}
                </div>
                <div>
                  <div className="text-white text-sm font-semibold">{course.instructor.name}</div>
                  <div className="text-gray-400 text-xs">{course.instructor.role}</div>
                </div>
              </div>
            </div>

            {/* Right - Sticky Card */}
            <div className="bg-white rounded-2xl p-6">
              <div className="relative w-full h-36 rounded-xl overflow-hidden mb-5">
                <img src={course.image} alt={course.title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-3xl font-black text-gray-900">${course.price}</span>
                <span className="text-gray-400 line-through text-sm">${course.originalPrice}</span>
                <span className="text-xs font-bold text-orange-500 bg-orange-50 px-2 py-0.5 rounded-full">-{discount}%</span>
              </div>
              <p className="text-xs text-gray-400 mb-4">Next cohort starts <strong className="text-gray-700">{course.nextStart}</strong></p>
              <button
                onClick={() => setShowModal(true)}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity mb-3"
              >
                Enroll Now for ${course.price}
              </button>
              <p className="text-xs text-gray-400 text-center mb-4">30-day money-back guarantee</p>
              <div className="flex flex-col gap-2">
                {course.includes.map((item) => (
                  <div key={item} className="flex items-center gap-2 text-xs text-gray-600">
                    <i className="ri-check-line text-emerald-500 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            {/* Tab Nav */}
            <div className="flex gap-1 bg-gray-100 rounded-full p-1 mb-8 w-fit">
              {(["overview", "curriculum", "instructor"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold capitalize cursor-pointer transition-all whitespace-nowrap ${
                    activeTab === tab ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Overview */}
            {activeTab === "overview" && (
              <div>
                <h2 className="text-2xl font-black text-gray-900 mb-4">About This Course</h2>
                <p className="text-gray-600 text-base leading-relaxed mb-8">{course.description}</p>
                <h3 className="text-lg font-bold text-gray-900 mb-4">What You&apos;ll Learn</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {course.outcomes.map((o) => (
                    <div key={o} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-orange-100 flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-orange-500 text-xs" />
                      </div>
                      {o}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Curriculum */}
            {activeTab === "curriculum" && (
              <div>
                <h2 className="text-2xl font-black text-gray-900 mb-6">Course Curriculum</h2>
                <div className="flex flex-col gap-3">
                  {course.curriculum.map((item, i) => (
                    <div key={i} className="flex items-center gap-4 bg-gray-50 rounded-xl px-5 py-4">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white text-xs font-black flex-shrink-0">
                        {i + 1}
                      </div>
                      <div>
                        <div className="text-xs text-orange-500 font-semibold">{item.week}</div>
                        <div className="text-sm font-semibold text-gray-900">{item.topic}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Instructor */}
            {activeTab === "instructor" && (
              <div>
                <h2 className="text-2xl font-black text-gray-900 mb-6">Your Instructor</h2>
                <div className="flex items-start gap-5 bg-gray-50 rounded-2xl p-6">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-black text-white bg-gradient-to-br ${course.instructor.color} flex-shrink-0`}>
                    {course.instructor.avatar}
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-gray-900">{course.instructor.name}</h3>
                    <p className="text-orange-500 text-sm font-semibold mb-3">{course.instructor.role}</p>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      An industry-leading expert with deep hands-on experience, {course.instructor.name} brings real-world knowledge and practical insights to every session. Known for making complex topics accessible and engaging.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-3">Ready to get started?</h2>
          <p className="text-gray-500 text-sm mb-6">Join {course.students.toLocaleString()}+ students already enrolled. Next cohort starts {course.nextStart}.</p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-base whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
          >
            Enroll Now for ${course.price}
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </section>

      {showModal && <EnrollModal course={course} onClose={() => setShowModal(false)} />}
    </main>
  );
}
